import React, { useRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import type { HeaderNavItem } from '../../types/headerNav';
import { getNavHref, isExternalNav, sortNavItems } from '../../utils/headerNavUtils';
import { HeaderNavIcon } from '../../utils/headerNavIcons';
import { getImageUrl } from '../../utils/imageUtils';

interface Props {
  items: HeaderNavItem[];
  fallbackCategories?: { label: string; slug: string }[];
}

const HeaderNavDesktop: React.FC<Props> = ({ items, fallbackCategories = [] }) => {
  const location = useLocation();
  const [openId, setOpenId] = useState<string | null>(null);
  const [megaPos, setMegaPos] = useState<{ top: number; left: number } | null>(null);
  const refs = useRef<Record<string, HTMLDivElement | null>>({});

  const navItems = sortNavItems(items.filter((i) => i.isEnabled !== false));

  useEffect(() => {
    const close = () => {
      setOpenId(null);
      setMegaPos(null);
    };
    window.addEventListener('scroll', close, { passive: true });
    return () => window.removeEventListener('scroll', close);
  }, []);

  const openPanel = (id: string) => {
    const el = refs.current?.[id];
    if (el) {
      const rect = el.getBoundingClientRect();
      setMegaPos({ top: rect.bottom, left: Math.max(8, rect.left) });
    }
    setOpenId(id);
  };

  const renderLink = (item: HeaderNavItem, className: string) => {
    const href = getNavHref(item);
    const active = !isExternalNav(item) && location.pathname === href;
    const style: React.CSSProperties = {
      color: active ? item.hoverTextColor || undefined : item.textColor || undefined,
      backgroundColor: active ? item.hoverBgColor || undefined : item.bgColor || undefined,
    };
    const content = (
      <>
        {item.thumbnailUrl && (
          <img src={getImageUrl(item.thumbnailUrl)} alt="" className="w-5 h-5 rounded object-cover" />
        )}
        <HeaderNavIcon name={item.icon} className="w-4 h-4 shrink-0" />
        <span>{item.label}</span>
        {item.badge && (
          <span
            className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white"
            style={{ backgroundColor: item.badgeColor || '#ef4444' }}
          >
            {item.badge}
          </span>
        )}
      </>
    );
    if (isExternalNav(item)) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={className} style={style}>
          {content}
        </a>
      );
    }
    return (
      <Link to={href} className={className} style={style} onClick={() => setOpenId(null)}>
        {content}
      </Link>
    );
  };

  if (!navItems.length && fallbackCategories.length) {
    return (
      <div className="flex items-center gap-1 py-3 overflow-x-auto">
        {fallbackCategories.map(({ label, slug }) => {
          const route = slug ? `/category/${slug}` : '/';
          return (
            <Link
              key={label}
              to={route}
              className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                location.pathname === route ? 'text-primary-600 bg-primary-50' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {label}
            </Link>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 py-3 overflow-x-auto min-w-0">
      {navItems.map((item) => {
        const hasChildren = (item.children?.length ?? 0) > 0;
        const isMega = item.menuType === 'mega';
        const isOpen = openId === item.id;

        return (
          <div key={item.id} ref={(el) => { refs.current[item.id] = el; }} className="relative shrink-0">
            {hasChildren || isMega ? (
              <button
                type="button"
                onClick={() => (isOpen ? setOpenId(null) : openPanel(item.id))}
                className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap flex items-center gap-1 transition-colors ${
                  isOpen ? 'text-primary-600 bg-primary-50' : 'text-gray-700 hover:bg-gray-100'
                }`}
                style={{ color: item.textColor, backgroundColor: item.bgColor }}
              >
                <HeaderNavIcon name={item.icon} className="w-4 h-4" />
                {item.label}
                {item.badge && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: item.badgeColor || '#ef4444' }}>
                    {item.badge}
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
            ) : (
              renderLink(
                item,
                `px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap flex items-center gap-1.5 transition-colors hover:bg-gray-100`
              )
            )}

            {isOpen && megaPos && !isMega && hasChildren && (
              <div
                className="fixed bg-white rounded-xl shadow-xl border border-gray-200 py-2 min-w-[220px] z-[9999] max-h-[70vh] overflow-y-auto"
                style={{ top: megaPos.top, left: megaPos.left }}
              >
                {sortNavItems(item.children!).map((child) => (
                  <div key={child.id} className="px-1">
                    {renderLink(
                      child,
                      'flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg w-full'
                    )}
                  </div>
                ))}
              </div>
            )}

            {isOpen && megaPos && isMega && (
              <div
                className="fixed bg-white rounded-xl shadow-2xl border border-gray-200 z-[9999] p-4 max-w-[min(96vw,900px)]"
                style={{ top: megaPos.top, left: Math.min(megaPos.left, window.innerWidth - 320) }}
              >
                <div className="flex gap-6 flex-wrap">
                  {(item.megaMenu?.columns || []).map((col, ci) => (
                    <div key={ci} className="min-w-[140px] flex-1">
                      {col.title && <p className="text-xs font-bold text-gray-500 uppercase mb-2">{col.title}</p>}
                      <ul className="space-y-1">
                        {sortNavItems(col.items || []).map((sub) => (
                          <li key={sub.id}>
                            {renderLink(sub, 'flex items-center gap-2 py-2 text-sm text-gray-700 hover:text-primary-600')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  {item.megaMenu?.promoImage && (
                    <div className="w-48 shrink-0 hidden md:block">
                      <Link to={item.megaMenu.promoLink || '#'} onClick={() => setOpenId(null)}>
                        <img
                          src={getImageUrl(item.megaMenu.promoImage)}
                          alt={item.megaMenu.promoTitle || 'Promo'}
                          className="rounded-lg w-full h-40 object-cover"
                        />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default HeaderNavDesktop;
